import { createUri, ensureDefined, getFromWindowRef, HpWebRequestError } from '@avantia/client-and-server-utilities';
import { v4 as uuid } from 'uuid';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logError, logInfo } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { setExperimentsInQuery } from '../tools/utilities';
import { HpDispatchFunction, HpFetchErrorPayload } from '../types';
import { HpFetchErrorOcurredAction } from '../types/ajaxCallActionTypes';
import { fetchResolver } from './fetchResolver';
import { HpAnswersPayload, HpGetAnswersActionPayload, HpWindowAnswerData } from './getAnswersActionTypes';
import { createQueryParamsIfNotProvided } from './getQuestionSetAction';

let websiteEndpoint: string;

registerConfigurationAction((config) => {
  ({ websiteEndpoint } = config);
});

export function getAnswersAction({
  productId,
  answersId,
  needs,
  metadata,
  queryParams,
  customHandler,
  setCustomAnswers
}: HpGetAnswersActionPayload): (dispatch: HpDispatchFunction<any>) => Promise<void> {
  if (location.hash === '') {
    const { answerData } = getFromWindowRef<HpWindowAnswerData>(['answerData']) || {};
    if (answerData) {
      return (dispatch: HpDispatchFunction<HpAnswersPayload>): Promise<void> => {
        return new Promise<void>((resolve) => {
          logInfo(`Retrieved answers from index page.`, HpClientDebugFlags.None);
          dispatch({ type: 'ANSWERS_RETRIEVED', payload: answerData });
          resolve();
        });
      };
    }
  }

  const prodId = encodeURIComponent(ensureDefined(productId, 'productId'));
  const asetId = encodeURIComponent(answersId || uuid());

  if (needs == 'answers' || needs.startsWith('answers-')) {
    return (dispatch: HpDispatchFunction<HpAnswersPayload>): Promise<void> => {
      queryParams = createQueryParamsIfNotProvided(metadata, queryParams);
      setExperimentsInQuery(queryParams);
      const url = createUri({
        uri: `${websiteEndpoint}/api/answers/${prodId}/${asetId}`,
        query: queryParams
      });
      return fetchResolver({
        options: { url, method: 'GET', description: `Get answers for ID ${asetId}` },
        dispatch,
        loadingPageType: 'page',
        successAction: (questionSet) => {
          if (isPayloadOkay(questionSet, dispatch, url)) {
            const { answers, questions } = questionSet;
            questionSet.answers = setCustomAnswers({
              answers,
              productId,
              questions,
              setAnswersId: (answersId) => (questionSet.answersId = answersId)
            });
            dispatchAnswers(questionSet, dispatch);
          }
        }
      });
    };
  } else if (customHandler) {
    const res = customHandler(dispatchAnswers);
    if (res) {
      return res;
    }
  }

  return () => Promise.resolve();
}

function isPayloadOkay(
  payload: HpAnswersPayload | HpWebRequestError<unknown>,
  dispatch: HpDispatchFunction<any>,
  url: string
): boolean {
  const reason = payload as HpWebRequestError<unknown>;
  if (reason.isHpError) {
    logError(`The call to ${url} failed: ${JSON.stringify(payload)}`);
    const { description, errorCode, message } = reason;
    const fetchError: HpFetchErrorPayload = {
      error: message,
      message: description,
      timestamp: new Date(),
      errorCode
    };
    const action: HpFetchErrorOcurredAction = { type: 'FETCH_ERROR_OCCURRED', fetchError };
    dispatch(action);
    return false;
  }

  return true;
}

export function dispatchAnswers(payload: HpAnswersPayload, dispatch: HpDispatchFunction<HpAnswersPayload>): void {
  dispatch({ type: 'ANSWERS_RETRIEVED', payload });
}
